import { useEffect, useState } from "react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { toast } from "sonner";
import { Modal } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Select } from "antd";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import {
  getAllCycle,
  getParcoursByID,
  updateParcours,
} from "../../../api/settings";

type UUID = string;
type Props = {
  parcoursID: UUID;
  show: boolean;
  onClose: () => void;
  onSaved: () => void;
};

type CycleOption = {
  id: string;
  label: string;
};

const EditParcoursModal = ({ parcoursID, show, onClose, onSaved }: Props) => {
  const [allCycle, setAllCycle] = useState<CycleOption[]>([]);
  const [selectedCycle, setSelectedCycle] = useState<CycleOption | null>(null);

  const createSchema = z.object({
    title: z.string().nonempty("Libellé du parcours requis"),
    id_cycle: z.string().nonempty("Cycle requis"),
  });

  type FormFields = z.infer<typeof createSchema>;
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
  });

  const selectedCycleId = watch("id_cycle");

  //Traitement après validation du formulaire de création d'. année académique
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      //Envoi des datas pour modification vers le backend
      await updateParcours(data, parcoursID);

      reset();
      setSelectedCycle(null);
      if (onSaved) onSaved(); // On fait un reload de la liste

      toast.success("Parcours mis à jour !"); //Toast de succès

      handleClose();
    } catch (error: any) {
      // Si erreur
      // console.log(error);
      setError("root", {
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      });
    }
  };

  useEffect(() => {
    if (!show || !parcoursID) return; // ✅ Ne rien faire si le modal n'est pas ouvert

    const fetchParcours = async () => {
      try {
        const [response, cycleData] = await Promise.all([
          getParcoursByID(parcoursID),
          getAllCycle(),
        ]);

        const parcours = response?.data?.parcours;

        if (!parcours) {
          throw new Error("Parcours introuvable");
        }

        const cycles = cycleData.data || [];
        setAllCycle(cycles);
        const currentCycle =
          cycles.find((item: CycleOption) => item.id === parcours.id_cycle) ||
          null;

        reset({
          title: parcours.title,
          id_cycle: parcours.id_cycle,
        });
        setSelectedCycle(currentCycle);
      } catch (error) {
        handleClose();
        // console.log(error)
      }
    };

    fetchParcours();
  }, [show, parcoursID, reset]);

  const handleClose = () => {
    reset();
    setSelectedCycle(null);
    onClose();
  };

  return (
    <>
      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Modifier un parcours </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form
            id="academic-year-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row row-cols-xxl-5 row-cols-md-6">
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Libellé
                  </label>
                  <input
                    {...register("title")}
                    type="text"
                    className={
                      errors.title ? "form-control is-invalid" : "form-control"
                    }
                    placeholder="Ex: Doctorat"
                  />
                  {errors.title && (
                    <div className="invalid-feedback">
                      {errors.title.message}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Cycle
                  </label>
                  <input type="hidden" {...register("id_cycle")} />
                  <Select
                    showSearch
                    allowClear
                    size="large"
                    optionFilterProp="label"
                    removeIcon={<span style={{ fontSize: 12 }}>×</span>}
                    style={{ width: "100%" }}
                    getPopupContainer={(trigger) =>
                      trigger.parentElement as HTMLElement
                    }
                    dropdownStyle={{ zIndex: 2000 }}
                    placeholder="Sélectionner un cycle"
                    value={selectedCycleId || undefined}
                    onChange={(value) => {
                      const cycle =
                        allCycle.find((item) => item.id === value) || null;
                      setSelectedCycle(cycle);
                      setValue("id_cycle", value || "", {
                        shouldValidate: true,
                      });
                    }}
                    onClear={() => {
                      setSelectedCycle(null);
                      setValue("id_cycle", "", { shouldValidate: true });
                    }}
                  >
                    {allCycle.map((cycle) => (
                      <Select.Option
                        key={cycle.id}
                        value={cycle.id}
                        label={`${cycle.label ?? ""}`}
                      >
                        {cycle.label}
                      </Select.Option>
                    ))}
                  </Select>
                  {errors.id_cycle && (
                    <div className="invalid-feedback d-block">
                      {errors.id_cycle.message}
                    </div>
                  )}
                </div>
              </div>
              {errors.root?.message && (
                <div className="col-xxl-12 col-xl-12 col-md-12">
                  <div className="alert alert-danger py-2">
                    {errors.root.message}
                  </div>
                </div>
              )}
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={handleClose}
          >
            Annuler
          </button>

          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="academic-year-form" // 🔗 liaison au formulaire
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  EditParcoursModal,
  [buildPermission(Resource.ACADEMIC_YEAR, Action.UPDATE)],
  "masquer",
);
